import type {
  FormattedScores,
  GamePlayerProjectionScore
} from '@library/types/scoring';
import type { ProjectionsAndScoresAPIResponse } from '@library/types/projection';
import { findProjectionByProjectionId } from '@bo-utils/projection';
import { getPlayerValues } from '@library/utils/players';
import { getPromotionValues } from '@library/utils';

export const getGamePlayerProjectionScore = (
  projections?: ProjectionsAndScoresAPIResponse
): FormattedScores | undefined => {
  if (!projections) {
    return undefined;
  }

  const gamePlayerProjectionScore: Array<GamePlayerProjectionScore> =
    projections?.data?.map((projection) => {
      const {
        id: projectionId,
        attributes: {
          auto_trade: autoTrade,
          board_time: boardTime,
          description,
          game_id: gameId,
          game_mode_ids: gameModeIds,
          has_suggested_lines: hasSuggestedLines,
          in_game: inGame,
          is_community_play: isCommunityPlay,
          line_score: projectionValue,
          max_probability_of_more: maxProbabilityOfMore,
          min_probability_of_more: minProbabilityOfMore,
          odds_type: oddsType,
          probability_of_more: probabilityOfMore,
          rank,
          start_time: startTime,
          display_time: displayTime,
          stat_type: statType,
          status,
          target_probability_of_more: targetProbabilityOfMore
        },
        relationships: {
          duration: { data: duration },
          stat_type: { data: statTypeData },
          league: { data: league },
          score: { data: scoreInfo }
        }
      } = projection;

      const scoreIncludedDetails = !scoreInfo
        ? undefined
        : projections.included.find(
            (detail) => detail.id === scoreInfo.id && detail.type === 'score'
          );

      const {
        market,
        playerId,
        playerImage,
        playerName,
        position,
        team,
        teamName,
        updatedAt
      } = getPlayerValues({
        projection,
        included: projections.included
      });

      const { discountPercentage, flashSaleLineScore, isPromo, optIn } =
        getPromotionValues({ included: projections.included, projection });

      const {
        attributes: {
          auto_finalize: autoFinalize,
          is_auto_settled: isAutoSettled,
          is_final: isFinal,
          is_off_the_board: isOffTheBoard,
          score,
          unders_win_dnp: reboot,
          details: scoreDetails
        },
        id: scoreId
      } = scoreIncludedDetails ?? { attributes: {} };

      const scores: GamePlayerProjectionScore = {
        autoFinalize: autoFinalize ?? false,
        autoTrade,
        boardTime,
        durationId: duration?.id ?? undefined,
        description,
        scoreDetails,
        discountPercentage,
        flashSaleLineScore,
        gameId,
        gameModeIds,
        hasSuggestedLines,
        isCommunityPlay,
        inGame,
        projectionId,
        imageUrl: playerImage ?? '',
        isAutoSettled,
        isFinal: isFinal ?? false,
        isPromo,
        isOffTheBoard: isOffTheBoard ?? false,
        leagueId: league?.id ?? undefined,
        market,
        maxProbabilityOfMore,
        minProbabilityOfMore,
        name: playerName,
        oddsType,
        optIn,
        position,
        playerId,
        probabilityOfMore,
        projectionValue: Number(projectionValue),
        rank,
        reboot,
        score: score ? Number(score) : '',
        scoreId,
        startTime,
        displayTime,
        statType,
        statTypeId: statTypeData?.id,
        status,
        targetProbabilityOfMore,
        team,
        teamName,
        updatedAt: updatedAt ?? ''
      };
      return scores;
    });
  return {
    paginationInfo: projections.meta,
    scores: gamePlayerProjectionScore
  };
};

export const getModifiedProjections = ({
  initialProjections,
  updatedProjections
}: {
  initialProjections: Array<GamePlayerProjectionScore>;
  updatedProjections: Array<Partial<GamePlayerProjectionScore>>;
}): Array<GamePlayerProjectionScore> =>
  updatedProjections.flatMap((change) => {
    const { projectionId } = change;

    const initialProjection = findProjectionByProjectionId({
      projectionArray: initialProjections,
      projectionId
    });

    if (!initialProjection) {
      return [];
    }

    return [
      {
        ...initialProjection,
        ...change
      } as GamePlayerProjectionScore
    ];
  });
